/**
 * The only way the core reaches the network. Each platform implements
 * {@link HttpTransport} with its own `fetch` (browser, React Native, desktop),
 * so the core never touches a global (ADR 0006).
 */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** A file handed over by {@link FilePickerPort}; `handle` is the platform's own object. */
export interface FileRef {
  readonly name: string;
  readonly type: string;
  readonly size: number;
  readonly handle: unknown;
}

export interface MultipartPart {
  readonly name: string;
  readonly value: string | FileRef;
}

/** A multipart body described declaratively; the adapter turns it into `FormData`. */
export interface MultipartBody {
  readonly parts: MultipartPart[];
}

export interface HttpRequest {
  readonly url: string;
  readonly method: HttpMethod;
  readonly headers: Readonly<Record<string, string>>;
  /** Serialised JSON, or a multipart description. */
  readonly body?: string | MultipartBody;
  readonly signal?: AbortSignal;
  /** Cookie handling in browsers; adapters without cookies ignore it. */
  readonly credentials?: 'include' | 'omit' | 'same-origin';
}

export interface ResponseHeaders {
  get(name: string): string | null;
}

export interface HttpResponse {
  readonly status: number;
  readonly ok: boolean;
  readonly headers: ResponseHeaders;
  text(): Promise<string>;
}

/** How much of a request body has left the device so far. */
export interface UploadProgress {
  readonly loaded: number;
  /** The whole body's size, or null when the platform cannot say. */
  readonly total: number | null;
}

export interface HttpTransport {
  send(request: HttpRequest): Promise<HttpResponse>;
  /**
   * The same request, reporting how much of the body has gone out. Optional,
   * because a platform whose HTTP client cannot report it (`fetch` cannot)
   * simply leaves it out and callers fall back to {@link HttpTransport.send}:
   * the upload still works, it just has no progress bar.
   */
  sendWithProgress?(
    request: HttpRequest,
    onProgress: (progress: UploadProgress) => void,
  ): Promise<HttpResponse>;
}

/** True for a multipart body, so the adapter knows not to send it as text. */
export function isMultipartBody(body: string | MultipartBody | undefined): body is MultipartBody {
  return typeof body === 'object' && body !== null && Array.isArray(body.parts);
}
